import React from 'react';
import { useTranslation } from 'react-i18next';
import Callout from '../../../helpContext/Callout';
import { KPIRelationship, WarehouseKPI } from '../kpiModel';
import KpiCard from './KpiCard';

interface RelatedKpiViewProps {
  primaryKpi: WarehouseKPI;
  relatedKpis: WarehouseKPI[];
  onNavigateToKpi?: (kpiId: string) => void;
  onShowVisualization?: (kpiId: string, visualizationType?: string) => void;
}

/**
 * Displays a grid of related KPI charts based on the primary KPI's relationships
 */
const RelatedKpiView: React.FC<RelatedKpiViewProps> = ({
  primaryKpi,
  relatedKpis,
  onNavigateToKpi,
  onShowVisualization,
}) => {
  const { t } = useTranslation('app');

  if (!primaryKpi.relationships || primaryKpi.relationships.length === 0) {
    return (
      <div className="bg-app-panel-stat mt-4 rounded p-4 text-center text-gray-400">
        {t('No related KPIs defined for {{primaryKpi}}', {
          primaryKpi: primaryKpi.title,
        })}
      </div>
    );
  }

  // Get relationship details for display
  const getRelationshipInfo = (
    relationship: KPIRelationship,
  ): { label: string; description: string } => {
    const relationType = relationship.relationshipType;
    let label = '';

    switch (relationType) {
      case 'causes':
        label = 'Directly affects';
        break;
      case 'influenced_by':
        label = 'Influenced by';
        break;
      case 'correlated':
        label = 'Correlated with';
        break;
      case 'cascade':
        label = 'Cascade with';
        break;
      case 'opposing':
        label = 'Trade-off with';
        break;
      case 'supporting':
        label = 'Supports';
        break;
      default:
        label = 'Related to';
    }

    return {
      label,
      description:
        relationship.description ||
        `This KPI is ${label.toLowerCase()} the related KPI.`,
    };
  };

  return (
    <div data-component="related-kpis" className="mt-4">
      <h3 className="mb-3 text-lg font-medium">
        {t('Related KPIs for {{primaryKpi}}', { primaryKpi: primaryKpi.title })}
      </h3>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {primaryKpi.relationships.map(relationship => {
          const relatedKpi = relatedKpis.find(
            kpi => kpi.id === relationship.relatedKpiId,
          );

          if (!relatedKpi) return null;

          const { label, description } = getRelationshipInfo(relationship);
          const strength = relationship.strength
            ? `(${Math.round(relationship.strength * 100)}% strength)`
            : '';

          return (
            <div
              key={relationship.relatedKpiId}
              className="flex flex-col rounded border border-gray-700"
            >
              <Callout className="bg-app-panel-dark p-2">
                <div className="flex justify-between">
                  <span className="font-medium">
                    {label} {strength}
                  </span>
                </div>
                <p className="text-xs text-gray-400">{description}</p>
              </Callout>
              <div className="flex-1">
                {relatedKpi && (
                  <KpiCard
                    kpi={relatedKpi}
                    relatedKpis={relatedKpis}
                    value={70} // Mock value - replace with real data
                    status="good" // Mock status - replace with real data
                    trend="up" // Mock trend - replace with real data
                    onNavigateToRelated={onNavigateToKpi}
                    onShowVisualization={onShowVisualization}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RelatedKpiView;
